import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, ChevronRight, Plus, Minus, MessageCircle,
  Building2, Fingerprint, User2, IdCard, Phone as PhoneIcon, Mail as MailIcon,
  Calendar, MapPin, Layers, Coins, MapPinned, Activity, Factory,
  Scale, Cpu, ShieldPlus, Landmark, Briefcase, ShieldCheck, Megaphone, PenTool,
} from "lucide-react";
import { useSeo } from "@/lib/seo";
import { BRAND } from "@/data/site";

const WA = BRAND.whatsapp.replace(/[^\d]/g, "");
const WA_URL = `https://wa.me/${WA}`;

const FIELDS = [
  { icon: Building2, name: "Company Name", d: "As registered with the Ministry of Corporate Affairs" },
  { icon: Fingerprint, name: "CIN", d: "Corporate Identity Number — unique 21-digit company identifier" },
  { icon: User2, name: "Director Name(s)", d: "Full name of every director on the board" },
  { icon: IdCard, name: "Director DIN", d: "Director Identification Number as filed with MCA" },
  { icon: PhoneIcon, name: "Registered Mobile Number", d: "Director's mobile as filed in MCA records" },
  { icon: MailIcon, name: "Email Address", d: "Company / director email as per official filings" },
  { icon: Calendar, name: "Date of Incorporation", d: "The exact date the company was registered" },
  { icon: MapPin, name: "State of Registration", d: "State where the company is incorporated" },
  { icon: Layers, name: "Business Type", d: "Pvt Ltd / LLP / OPC / Public Ltd / Section 8" },
  { icon: Coins, name: "Paid-up Capital", d: "Authorized and paid-up capital at registration" },
  { icon: MapPinned, name: "Registered Address", d: "Full registered office address including pincode" },
  { icon: Activity, name: "Company Status", d: "Active, Struck Off, Dormant or Under Liquidation" },
  { icon: Factory, name: "Industry Category", d: "NIC code based industry classification" },
];

const STATS_YEAR = [
  { y: "2022", r: "7,50,000+", g: "—" },
  { y: "2023", r: "8,50,000+", g: "+13%" },
  { y: "2024", r: "9,80,000+", g: "+15%" },
  { y: "2025", r: "11,20,000+", g: "+14%" },
  { y: "2026", r: "12,50,000+ (projected)", g: "+12%" },
];

const USE_CASES = [
  { icon: Scale, t: "CA & CS Firms", d: "Reach newly incorporated companies for GST registration, compliance, audit and annual filing services. Every new company needs a CA." },
  { icon: Cpu, t: "Software & SaaS", d: "Sell accounting software, ERP systems, CRM tools and business management platforms to freshly started companies." },
  { icon: PenTool, t: "Trademark & IP", d: "Contact new companies for trademark registration, logo protection, brand name filing and intellectual property services." },
  { icon: Landmark, t: "Banks & NBFCs", d: "Offer business loans, current accounts, startup credit lines and financial products to newly registered entities." },
  { icon: Briefcase, t: "Business Consultants", d: "Provide compliance advisory, virtual office, tax planning and startup consulting to new companies." },
  { icon: ShieldPlus, t: "Insurance Companies", d: "Pitch group health insurance, fire insurance, liability coverage and employee insurance to new businesses." },
  { icon: ShieldCheck, t: "Legal Firms", d: "Offer legal retainership, contract drafting, regulatory compliance and dispute resolution to new companies." },
  { icon: Megaphone, t: "Marketing Agencies", d: "Build targeted B2B campaigns using company type, location, industry and capital data for precise audience targeting." },
];

const PRICING = [
  { t: "Trial Pack", price: "₹3,000", period: "one-time", d: "1-day MCA data sample, up to 500 records. Excel/CSV — verify quality before committing to a monthly plan.", cta: "Order Trial", href: WA_URL, external: true, featured: false },
  { t: "Monthly Plan", price: "₹20,000", period: "per month", d: "Daily MCA data delivery, all states, all business types, unlimited records, Excel/CSV, WhatsApp support.", cta: "Order Now", href: WA_URL, external: true, featured: true, badge: "Most Popular" },
  { t: "Annual Plan", price: "Contact for Best Price", period: "12 months", d: "12 months of daily MCA data, priority support, custom filters and optional API access for enterprise teams.", cta: "Get Quote", href: "/contact", external: false, featured: false },
];

const SAMPLE_ROWS = [
  { c: "Nexgen Tech Solutions Pvt Ltd", cin: "U72200MH2026PTC456789", dir: "Rajesh Kumar", mob: "98765XXXXX", em: "rajesh@nexgentech.in", s: "Maharashtra", d: "15-Jan-2026", t: "Pvt Ltd" },
  { c: "Greenfield Agro LLP", cin: "AAB-1234", dir: "Priya Sharma", mob: "87654XXXXX", em: "priya@greenfieldagro.in", s: "Punjab", d: "18-Jan-2026", t: "LLP" },
  { c: "CloudBridge IT Services Pvt Ltd", cin: "U62000KA2026PTC789012", dir: "Amit Patel", mob: "90123XXXXX", em: "amit@cloudbridge.co.in", s: "Karnataka", d: "20-Jan-2026", t: "Pvt Ltd" },
  { c: "Dharma Legal Associates OPC", cin: "U74999DL2026OPC345678", dir: "Sneha Reddy", mob: "81234XXXXX", em: "sneha@dharmalegal.in", s: "Delhi", d: "22-Jan-2026", t: "OPC" },
  { c: "Pacific Trading Co Pvt Ltd", cin: "U51000TN2026PTC567890", dir: "Mohammed Faiz", mob: "93456XXXXX", em: "faiz@pacifictrading.in", s: "Tamil Nadu", d: "25-Jan-2026", t: "Pvt Ltd" },
  { c: "Silverline Ventures LLP", cin: "AAC-5678", dir: "Ananya Iyer", mob: "94567XXXXX", em: "ananya@silverlineventures.in", s: "Gujarat", d: "27-Jan-2026", t: "LLP", blur: true },
  { c: "Orbit Wellness Pvt Ltd", cin: "U74140UP2026PTC112233", dir: "Vikram Joshi", mob: "89012XXXXX", em: "vikram@orbitwellness.in", s: "Uttar Pradesh", d: "29-Jan-2026", t: "Pvt Ltd", blur: true },
  { c: "Harbour Logistics OPC", cin: "U60300WB2026OPC998877", dir: "Deepa Ghosh", mob: "82345XXXXX", em: "deepa@harbourlogistics.in", s: "West Bengal", d: "30-Jan-2026", t: "OPC", blur: true },
];

const COMPARISON = [
  { f: "Data Freshness", d: "Daily Updated", a: "Weekly", b: "Monthly", cc: "One-time dump" },
  { f: "Director Mobile", d: "Verified", a: "—", b: "Partial", cc: "—" },
  { f: "Director Email", d: "Yes", a: "Yes", b: "—", cc: "—" },
  { f: "Director DIN", d: "Yes", a: "—", b: "—", cc: "—" },
  { f: "Company Status Filter", d: "Yes", a: "—", b: "Yes", cc: "—" },
  { f: "State-wise Filter", d: "Yes", a: "—", b: "Yes", cc: "—" },
  { f: "Business Type Filter", d: "Yes", a: "—", b: "—", cc: "—" },
  { f: "Excel / CSV Download", d: "Yes", a: "Yes", b: "Yes", cc: "PDF only" },
  { f: "WhatsApp Support", d: "Yes", a: "—", b: "—", cc: "Email only" },
  { f: "Trial Available", d: "Yes — ₹3,000", a: "—", b: "—", cc: "—" },
];

const STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
];
const UTS = [
  "Delhi","Chandigarh","Puducherry","Jammu & Kashmir","Ladakh","Lakshadweep",
  "Andaman & Nicobar Islands","Dadra & Nagar Haveli and Daman & Diu",
];

const FAQS = [
  { q: "What is MCA company database?", a: "MCA company database is a collection of registration details of all companies incorporated with the Ministry of Corporate Affairs in India. It includes company name, CIN, director details, mobile, email, registration date, business type, and more. This data is used by B2B sales teams, CA firms, banks, and marketing agencies to reach newly registered businesses." },
  { q: "How to download MCA registered company list?", a: "You can purchase and download MCA registered company data from Datorra India. We deliver the data in Excel (.xlsx) and CSV formats that you can open in any spreadsheet software or import into your CRM. Simply contact us on WhatsApp at +91 85379 42196 to place your order." },
  { q: "What data fields are included in MCA database?", a: "Our MCA database includes 13 data fields: Company Name, CIN, Director Name(s), Director DIN, Mobile Number, Email Address, Date of Incorporation, State, Business Type, Paid-up Capital, Registered Address, Company Status, and Industry Category (NIC Code)." },
  { q: "Is buying MCA database legal?", a: "Yes, buying an MCA company database is completely legal. MCA registration data is sourced from publicly available government records filed under the Companies Act. Using this data for legitimate business purposes like B2B sales, marketing outreach, and professional services is legal in India." },
  { q: "How fresh is Datorra's MCA data?", a: "Datorra delivers MCA data that is updated every 24 hours. You receive data of companies that were incorporated yesterday or today. This means you get the freshest leads in the market — before your competitors even know these companies exist." },
  { q: "What is the price of MCA company database?", a: "Our MCA database pricing starts at ₹3,000 for a trial pack (1-day data, up to 500 records). The monthly plan is ₹20,000 per month for daily data delivery with all states and business types included. Annual plans are available at discounted rates — contact us for pricing." },
  { q: "Can I get MCA data for a specific state?", a: "Yes, Datorra offers state-wise filtering at no extra cost. You can request MCA data for any specific state — Maharashtra, Delhi, Karnataka, Tamil Nadu, or any other state. You can also combine multiple states in a single order." },
  { q: "What business types are included?", a: "Our MCA database includes all types of companies registered with MCA: Private Limited (Pvt Ltd), Limited Liability Partnership (LLP), One Person Company (OPC), Public Limited Company, Section 8 Companies, and Nidhi Companies. Pvt Ltd companies make up about 70% of all registrations." },
  { q: "In what format is MCA data delivered?", a: "MCA data is delivered in Excel (.xlsx) and CSV formats. These formats are compatible with Microsoft Excel, Google Sheets, LibreOffice, and can be directly imported into CRM tools like Salesforce, HubSpot, Zoho, or any calling software." },
  { q: "How many new companies register with MCA every month?", a: "India sees over 50,000 new company registrations with MCA every month. This number has been steadily growing — from about 7.5 lakh in 2022 to a projected 12.5 lakh+ in 2026. This means a massive, continuously renewing pool of fresh B2B leads." },
];

const EXPLORE = [
  { l: "GST Registered Company Database", h: "/databases" },
  { l: "GST Sales & Purchase Data", h: "/gst-sales-purchase-data" },
  { l: "MCA Data by Month", h: "/mca-data" },
  { l: "Contact Our Sales Team", h: "/contact" },
];

export default function McaData() {
  const [openFaq, setOpenFaq] = useState(0);

  useSeo({
    title: "MCA Company Database India | Newly Registered Company Data | Datorra",
    description:
      "Buy verified MCA registered company database in India. Newly incorporated companies with director name, mobile, email, CIN. Pan-India coverage. Download in Excel/CSV. Call +91 85379 42196",
    path: "/mca-company-database",
    jsonLd: [
      {
        id: "mca-breadcrumb",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://datorra.in/" },
            { "@type": "ListItem", position: 2, name: "MCA Database", item: "https://datorra.in/mca-company-database" },
          ],
        },
      },
      {
        id: "mca-product",
        data: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "MCA Company Database India",
          description:
            "Verified MCA registered company database — newly incorporated companies with director name, DIN, mobile, email, CIN, business type and NIC industry code. Daily updates across Pan India.",
          brand: { "@type": "Brand", name: "Datorra India" },
          category: "Business Data / MCA Company Database",
          areaServed: "IN",
          offers: [
            { "@type": "Offer", name: "Trial Pack", price: "3000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/mca-company-database" },
            { "@type": "Offer", name: "Monthly Plan", price: "20000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/mca-company-database" },
            { "@type": "Offer", name: "Annual Plan", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/mca-company-database" },
          ],
        },
      },
      {
        id: "mca-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      },
    ],
  });

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="mca-data-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 border-b border-slate-100 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-50/60 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-slate-500 flex items-center gap-1.5">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <span className="text-slate-900 font-medium">MCA Database</span>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-slate-900 leading-[1.03] max-w-5xl"
          >
            MCA Company Database India — <span className="font-serif-editorial italic">newly registered</span> companies, daily updated
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 leading-relaxed"
          >
            Access verified data of newly incorporated companies from the <strong className="font-medium text-slate-900">Ministry of Corporate Affairs</strong>. Get director
            name, mobile number, email, CIN and complete registration details — delivered daily in Excel/CSV.
            Trusted by CA firms, banks and B2B sales teams across India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" data-testid="mca-hero-sample" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 active:scale-95 transition-all">
              Get Sample Data
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </a>
            <button onClick={scrollToPricing} data-testid="mca-hero-pricing" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 hover:bg-slate-50 active:scale-95 transition-all">
              View Pricing
              <ChevronRight className="w-4 h-4 rotate-90" strokeWidth={1.75} />
            </button>
          </motion.div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600">
            {["Daily Updated", "13 Data Fields", "Pan India", "Excel / CSV"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" strokeWidth={2.25} />
                <span className="font-medium text-slate-800">{b}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — What is MCA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Overview</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            What is <span className="font-serif-editorial italic">MCA Company Database</span>?
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8">
            {[
              { icon: Landmark, t: "The Government Registry", d: "MCA stands for the Ministry of Corporate Affairs — the Government of India body responsible for registering every company in the country." },
              { icon: Building2, t: "Every Registered Business", d: "Every company incorporated in India — Private Limited, LLP, OPC, Public Limited — must file its registration with MCA under the Companies Act." },
              { icon: Fingerprint, t: "Complete Registration Records", d: "The MCA database contains company name, CIN, directors, DIN, mobile, email, capital, address, industry code and filing status." },
              { icon: Megaphone, t: "Invaluable for B2B Outreach", d: "For B2B sales, marketing and business development, MCA data is the single most reliable source of company registration data India can offer." },
              { icon: Cpu, t: "Ready-to-Use with Datorra", d: "Datorra provides this data in clean, structured, ready-to-use format — refreshed every 24 hours from official filings." },
              { icon: Activity, t: "50,000+ New Leads Every Month", d: "Over 50,000 new companies register with MCA every single month — that's 50,000 fresh B2B leads to work every 30 days." },
            ].map((r) => (
              <div key={r.t} className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                  <r.icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-medium text-slate-900">{r.t}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{r.d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 active:scale-95 transition-all">
              Start Reaching Newly Registered Companies
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Data Fields */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Data Fields</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Complete data fields in our MCA Database
          </h2>
          <p className="mt-6 max-w-2xl text-slate-600 text-base md:text-lg leading-relaxed">
            Every record ships with these 13 verified fields — everything you need to identify, segment and reach the right newly registered company.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FIELDS.map((f, i) => (
              <div key={f.name} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-start gap-4 hover:border-blue-100 hover:shadow-md transition-all">
                <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-medium">/{String(i + 1).padStart(2, "0")}</span>
                    <div className="font-display text-base font-medium text-slate-900">{f.name}</div>
                  </div>
                  <div className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — Trends & Stats */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Trends</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            MCA company registration trends in India
          </h2>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { n: "50,000+", l: "New companies register with MCA every month" },
              { n: "10,00,000+", l: "New companies incorporated in 2026 YTD" },
              { n: "#3", l: "Largest startup ecosystem in the world" },
              { n: "70%+", l: "Pvt Ltd share of all new registrations" },
              { n: "+25%", l: "YoY growth in LLP registrations" },
              { n: "4 states", l: "Maharashtra, Delhi, Karnataka, Tamil Nadu lead" },
            ].map((s) => (
              <div key={s.n} className="bg-[#fafafa] border border-slate-100 rounded-2xl p-6">
                <div className="font-display text-3xl md:text-4xl font-light text-slate-900">{s.n}</div>
                <div className="mt-2 text-sm text-slate-500 leading-relaxed">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 overflow-hidden rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="p-4 font-medium">Year</th>
                  <th className="p-4 font-medium">New Registrations</th>
                  <th className="p-4 font-medium">Growth</th>
                </tr>
              </thead>
              <tbody>
                {STATS_YEAR.map((r, i) => (
                  <tr key={r.y} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-white" : "bg-slate-50/40"}`}>
                    <td className="p-4 font-medium text-slate-900">{r.y}</td>
                    <td className="p-4 text-slate-700">{r.r}</td>
                    <td className="p-4 text-blue-600 font-medium">{r.g}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-400">Source: MCA public records and industry reports.</p>
        </div>
      </section>

      {/* SECTION 5 — Use Cases */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Use Cases</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Who benefits from MCA company database?
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {USE_CASES.map((u) => (
              <div key={u.t} className="bg-white border border-slate-100 rounded-3xl p-7 hover:shadow-md hover:border-blue-100 transition-all flex flex-col">
                <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <u.icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-display text-lg font-medium text-slate-900">{u.t}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — Pricing */}
      <section id="pricing" className="py-20 md:py-28 bg-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Pricing</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Simple, <span className="font-serif-editorial italic">transparent</span> pricing
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRICING.map((p) => (
              <div key={p.t} className={`relative rounded-3xl p-8 flex flex-col ${p.featured ? "bg-slate-900 text-white border border-slate-900 shadow-2xl shadow-slate-900/20" : "bg-white border border-slate-100"}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold">
                    {p.badge}
                  </span>
                )}
                <h3 className={`font-display text-2xl font-medium ${p.featured ? "text-white" : "text-slate-900"}`}>{p.t}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className={`font-display text-3xl md:text-4xl font-light ${p.featured ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                  <span className={`text-sm ${p.featured ? "text-slate-300" : "text-slate-500"}`}>{p.period}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-slate-300" : "text-slate-600"}`}>{p.d}</p>
                {p.external ? (
                  <a href={p.href} target="_blank" rel="noopener noreferrer" className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                    {p.cta}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </a>
                ) : (
                  <Link to={p.href} className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                    {p.cta}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </Link>
                )}
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-slate-500 text-sm">
            All prices exclusive of GST. Custom packages available for state-specific or industry-specific data.
          </p>
        </div>
      </section>

      {/* SECTION 7 — Sample Data */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Sample</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            See the quality of our MCA data
          </h2>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  {["Company Name","CIN","Director","Mobile","Email","State","Reg Date","Type"].map((h) => (
                    <th key={h} className="p-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SAMPLE_ROWS.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className={`p-4 text-slate-900 font-medium ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.c}</td>
                    <td className={`p-4 text-slate-700 font-mono text-xs ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.cin}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.dir}</td>
                    <td className={`p-4 text-slate-700 font-mono text-xs ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.mob}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.em}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.s}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.d}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.t}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-col items-center gap-3 text-center">
            <p className="text-slate-600">Want to see the complete, unblurred dataset? Request a sample now.</p>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-500/20 transition-all">
              Request Sample on WhatsApp
              <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Comparison */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Compare</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Why choose Datorra over other MCA data providers?
          </h2>

          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em]">
                  <th className="p-5 font-medium text-slate-500">Feature</th>
                  <th className="p-5 font-display font-medium text-slate-900 bg-blue-50 border-l border-blue-100">
                    <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-600" />Datorra India</span>
                  </th>
                  <th className="p-5 font-medium text-slate-500">Provider A</th>
                  <th className="p-5 font-medium text-slate-500">Provider B</th>
                  <th className="p-5 font-medium text-slate-500">Provider C</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={row.f} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="p-5 font-medium text-slate-700">{row.f}</td>
                    <td className="p-5 bg-blue-50/60 border-l border-blue-100">
                      <span className="inline-flex items-center gap-2 text-slate-900 font-medium">
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2.25} />
                        {row.d}
                      </span>
                    </td>
                    {[row.a, row.b, row.cc].map((v, j) => (
                      <td key={j} className="p-5 text-slate-500">
                        {v === "—" ? <span className="text-slate-300">—</span> : <span>{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SECTION 9 — Coverage */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Coverage</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Pan-India coverage — all 28 states &amp; 8 union territories
          </h2>

          <h3 className="mt-12 text-sm uppercase tracking-[0.16em] font-semibold text-slate-500">States (28)</h3>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {STATES.map((s) => (
              <span key={s} className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-800 hover:border-blue-600 hover:text-blue-700 transition-colors">
                <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" strokeWidth={1.75} />
                <span className="truncate">{s}</span>
              </span>
            ))}
          </div>

          <h3 className="mt-10 text-sm uppercase tracking-[0.16em] font-semibold text-slate-500">Union Territories (8)</h3>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {UTS.map((u) => (
              <span key={u} className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm text-slate-800">
                <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" strokeWidth={1.75} />
                <span className="truncate">{u}</span>
              </span>
            ))}
          </div>

          <p className="mt-8 text-slate-500 text-sm">
            Data available for all states and UTs. State-wise filtering included in every plan.
          </p>
        </div>
      </section>

      {/* SECTION 10 — FAQ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ FAQ</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Frequently asked questions about MCA Company Database
          </h2>

          <div className="mt-12 divide-y divide-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button
                    data-testid={`mca-faq-toggle-${i}`}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    aria-expanded={open}
                    className="w-full py-6 flex items-start justify-between gap-6 text-left group"
                  >
                    <span className="font-display text-lg md:text-xl font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                      {f.q}
                    </span>
                    <span className="mt-1 flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center transition-colors group-hover:border-slate-900">
                      {open ? <Minus className="w-4 h-4 text-slate-900" strokeWidth={1.75} /> : <Plus className="w-4 h-4 text-slate-900" strokeWidth={1.75} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.2, 0.7, 0.15, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-16 text-slate-600 leading-relaxed text-base md:text-lg">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 11 — Long-form SEO Content */}
      <section className="py-20 md:py-28 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Playbook</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            How to use MCA company database for B2B sales in India
          </h2>

          <div className="mt-12 space-y-10 text-base md:text-lg text-slate-700 leading-relaxed">
            <p>
              The MCA company database is one of the most under-utilised assets in Indian B2B. Every company that gets
              incorporated in the country has to register with the <strong className="text-slate-900">Ministry of Corporate Affairs</strong>,
              which means MCA fresh leads India is effectively a real-time feed of every business that just opened its doors.
              If your product or service is bought by companies — CA firms, banks, SaaS platforms, insurers, legal firms —
              then the MCA registered company list India is the fastest path from cold market to warm pipeline.
            </p>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Understanding MCA data — what it contains and why it matters</h3>
              <p className="mt-4">
                An MCA company database includes registration details filed under the Companies Act — company name, CIN,
                director name, DIN, mobile, email, incorporation date, paid-up capital, registered address, business type
                (Pvt Ltd, LLP, OPC, Public Ltd) and NIC industry code. This is a company registration database India that
                doubles as an MCA company contact database — you get the person who actually filed the incorporation and can
                sign off on your services.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Step 1 — Identify your target audience</h3>
              <p className="mt-4">
                Start by defining your ICP against MCA filters: business type (Pvt Ltd company database India, LLP company
                database India), state, industry code and paid-up capital range. A CA firm might target Pvt Ltd companies
                registered in the last 30 days with paid-up capital of ₹1 lakh–₹10 lakh in metro states. A SaaS company might
                target LLP company database India records in IT services with two or more directors.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Step 2 — Filter and segment your MCA data</h3>
              <p className="mt-4">
                Once you buy MCA database access from a serious MCA data provider India, segment by geography and industry.
                Split the file into three tiers — hot (registered in last 7 days), warm (last 8–30 days), cold (30+ days).
                Recency is everything: new company registration data India is most valuable in the first month, because
                that's when new founders are actively evaluating vendors.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Step 3 — Craft personalised outreach</h3>
              <p className="mt-4">
                Reference the exact business type and registration date in your first message. "Congratulations on
                incorporating [Company Name] on [date] — as a Pvt Ltd, you'll need to file your first ROC compliance within
                180 days" is dramatically more effective than a generic cold call. Because you're using MCA company data
                download from a director database India, you have the director's own mobile — skip the gatekeeper.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Step 4 — Multi-channel sequences</h3>
              <p className="mt-4">
                Combine calling, WhatsApp, email and LinkedIn. Day 1 — WhatsApp introduction referencing incorporation. Day 3
                — email with a one-pager. Day 5 — LinkedIn connection request with a personal note. Day 7 — follow-up call.
                MCA data gives you the phone, email and name; the sequence gives you the conversion.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Step 5 — Track results and optimise</h3>
              <p className="mt-4">
                Tag every lead by state, business type, paid-up capital band and incorporation age. After two months you'll
                see which segments convert — most teams find that ₹5–50 lakh paid-up Pvt Ltd companies in metros are the
                sweet spot. Double down there. Move the low-converting cohorts to a slower nurture cadence.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Industry examples</h3>
              <p className="mt-4">
                <strong className="text-slate-900">CA and CS firms</strong> use MCA data to sell first-year compliance packages —
                GST registration, book-keeping, TDS returns, annual ROC filings. <strong className="text-slate-900">SaaS platforms</strong> pitch
                CRM, ERP and accounting software during the first quarter when new founders are still selecting stacks.
                <strong className="text-slate-900"> Banks and NBFCs</strong> offer current accounts and startup credit lines. Each vertical
                converts differently — but all of them start with the same MCA company data download.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Why fresh data beats old databases</h3>
              <p className="mt-4">
                A 6-month-old MCA list is worthless — the company has already picked its CA, its bank and its software. Daily
                updated data from a serious MCA data provider India is what separates a converting outbound motion from a
                slow burn. Datorra refreshes the ministry of corporate affairs database every 24 hours so your team is always
                working the freshest cohort.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Compliance — best practices</h3>
              <p className="mt-4">
                MCA registration data is public information under the Companies Act. Use it for legitimate B2B outreach,
                honour DND registrations, keep opt-outs current, and sign a data-use agreement with your provider. This is
                how professional teams work — respect the recipient's time and the compliance envelope, and results follow.
              </p>
            </div>

            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Conclusion</h3>
              <p className="mt-4">
                An MCA registered company list India isn't a spreadsheet — it's a pipeline generator. Bought fresh, filtered
                tightly and worked with a proper multi-channel sequence, it can carry an outbound team for years. If you're
                ready to start, request a sample from Datorra on WhatsApp — you'll have your first cohort in Excel within 24 hours.
              </p>
              <div className="mt-6">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 transition-all">
                  Get Started Today
                  <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 12 — Explore More */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ More</div>
          <h2 className="mt-4 font-display text-2xl md:text-4xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Explore more data products
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {EXPLORE.map((l) => (
              <Link key={l.l} to={l.h} className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-5 hover:border-slate-900 hover:shadow-md transition-all">
                <span className="font-medium text-slate-900 text-sm">{l.l}</span>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.75} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA BANNER */}
      <section className="relative py-20 md:py-24 bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-16 -left-16 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Ready to reach India's <span className="font-serif-editorial italic">newly incorporated</span> companies?
          </h2>
          <p className="mt-6 text-blue-200">Daily MCA data delivery — trusted by CA firms, banks and B2B sales teams.</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-7 pr-6 py-5 text-base font-medium hover:brightness-95 hover:shadow-xl hover:shadow-green-500/25 active:scale-95 transition-all">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
              Talk to Sales on WhatsApp
            </a>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 pl-7 pr-6 py-5 text-base font-medium hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all">
              Request Access
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
